import { Instant, LocalDateTime } from "@js-joda/core";
import Modal from "@material-ui/core/Modal/Modal";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TextArea } from "components/designV2/textarea/textarea";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { TimeReportDto } from "model/TimeReport";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { getWorkHours } from "utils/utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Row } from "../timeReport/monthlyOverview.styled";
import { FlexRow } from "../timeReport/timeReportModal.styled";
import {
  calculateTotalTimeString,
  createStartAndEndDateStrings,
} from "../timeReport/timeReportsUtils";
import { Text, TimeReportModal } from "./clockStamp.styled";

type Props = {
  showTimeReportModal: boolean;
  setShowTimeReportModal: React.Dispatch<React.SetStateAction<boolean>>;
  timereport: TimeReportDto;
  setTimeReportToTimeReport: React.Dispatch<
    React.SetStateAction<TimeReportDto | undefined>
  >;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  setKeyShouldBeDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setShowWarningModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ClockStampTimeReportModal: React.FC<Props> = ({
  showTimeReportModal,
  setShowTimeReportModal,
  timereport,
  setTimeReportToTimeReport,
  phoneNumber,
  setPhoneNumber,
  setKeyShouldBeDisabled,
  setShowWarningModal,
}) => {
  const { t } = useTranslation();
  const ShiftFormHHMMRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
  const [companyState] = useCompanyStore();
  const [loading, setLoading] = useState(false);
  const [, , alertDispatch] = useAlertStore();
  const [userNotes, setUserNotes] = useState("");

  const toLocalHHMM = (iso: string | undefined) => {
    if (!iso) return "";
    const date = new Date(iso);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const startTime = LocalDateTime.ofInstant(
    Instant.parse(timereport.startTime),
  );

  const allowEditSetting =
    companyState.companySettings?.find(
      (s: any) => s.key === "CompanyStampClockAllowEditTime",
    )?.value ?? "false";

  const canEditTimes = allowEditSetting === "true";

  const [startTimeStr, setStartTimeStr] = useState(
    toLocalHHMM(
      timereport.checkInTime ? timereport.checkInTime : timereport.startTime,
    ),
  );
  const [endTimeStr, setEndTimeStr] = useState(
    toLocalHHMM(
      timereport.checkOutTime ? timereport.checkOutTime : timereport.endTime,
    ),
  );

  const originalBreakTime = (timereport as any).breakTime ?? 0;
  const originalBreakStartTime = (timereport as any).breakStartTime;
  const originalBreakEndTime = (timereport as any).breakEndTime;

  const { localStartDateStr, localEndDateStr } = createStartAndEndDateStrings(
    startTime,
    startTimeStr,
    endTimeStr,
  );

  const totalTimeStr = calculateTotalTimeString(
    localStartDateStr,
    localEndDateStr,
    originalBreakTime,
    t,
  );

  const handleCloseModal = () => {
    setShowTimeReportModal(false);
    setTimeReportToTimeReport(undefined);
    setPhoneNumber("");
    setKeyShouldBeDisabled(false);
  };

  const createISOFromTimeString = (timeStr: string, baseDate: string) => {
    const date = new Date(baseDate);
    const [hours, minutes] = timeStr.split(":");
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date.toISOString();
  };

  const d = LocalDateTime.ofInstant(Instant.parse(timereport.startTime));
  const date = `${t(
    `WeekDays.${d.dayOfWeek().name()}SHORT`,
  )} ${d.dayOfMonth()}/${d.monthValue()}`;

  const handleSubmit = () => {
    if (
      !timereport.id ||
      !companyState.company?.id ||
      loading ||
      !phoneNumber
    )
      return;

    const data = {
      startTime: canEditTimes
        ? createISOFromTimeString(startTimeStr, timereport.startTime)
        : timereport.startTime,
      endTime: canEditTimes
        ? createISOFromTimeString(endTimeStr, timereport.endTime)
        : timereport.endTime,
      userNotes: userNotes,
      breakTime: originalBreakTime,
      breakStartTime: originalBreakStartTime,
      breakEndTime: originalBreakEndTime,
    };

    const cleanedPhoneNumber = phoneNumber.startsWith("+46")
      ? phoneNumber.substring(1)
      : phoneNumber;

    setLoading(true);

    Api()
      .company.clockStampTimeReport(
        companyState.company.id,
        cleanedPhoneNumber,
        timereport.id,
        data,
      )
      .then(() => {
        setLoading(false);
        handleCloseModal();
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            title: t("CompanyTimeReport.TimeReported") + "!",
            message: "",
          },
        });
      })
      .catch(() => {
        setLoading(false);
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("RegisterCompany.SomethingWentWrong"),
          },
        });
      });
  };

  if (!timereport) {
    return (
      <Modal open={showTimeReportModal} onClose={handleCloseModal}>
        <TimeReportModal>
          <Text> {t("CompanyTimeReport.NoDataAvailable")}</Text>
        </TimeReportModal>
      </Modal>
    );
  }

  const handleModalCloseWithReason = (
    _event: object,
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick") {
      setShowWarningModal(true);
      return;
    }
    handleCloseModal();
  };

  return (
    <Modal
      open={showTimeReportModal}
      onClose={handleModalCloseWithReason}
      aria-labelledby="time-report-modal"
      aria-describedby="time-report-modal-description"
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <TimeReportModal>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <SecondaryButton
            iconName="v2/cross"
            aria-label={t("DesignComponents.CloseModal")}
            size="small"
            onClick={handleCloseModal}
          />
        </div>
        {loading && (
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            fontSize={FontSize.H4}
          >
            {t("CompanyTimeReport.Loading")}
          </Text>
        )}
        {!loading && (
          <>
            <Text typography="h2" color={Color.PitchBlack}>
              {t("CompanyTimeReport.TimeReport")}
            </Text>
            <Text
              fontFamily={FontFamily.MontserratRegular}
              color={Color.PitchBlack}
              fontSize={FontSize.Small}
            >
              {t("StampClock.TimeReportInfo")}
            </Text>
            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                color={Color.PitchBlack}
                fontSize={FontSize.H4}
              >
                {t("StampClock.ScheduledTime")}
              </Text>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.H4}
                color={Color.PitchBlack}
              >
                {getWorkHours(t, ...[timereport])}
              </Text>
            </FlexRow>

            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                color={Color.PitchBlack}
                fontSize={FontSize.H4}
              >
                {t("StampClock.ReportedTime")}
              </Text>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.H4}
                color={Color.PitchBlack}
              >
                {totalTimeStr}
              </Text>
            </FlexRow>

            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                fontSize={FontSize.H4}
              >
                {t("CompanyTimeReport.Date")}
              </Text>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.H4}
              >
                {date}
              </Text>
            </FlexRow>

            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                fontSize={FontSize.H4}
              >
                {t("StampClock.ReportedStarttime")}
              </Text>
              <div style={{ marginBottom: 12 }}>
                <TimePicker
                  ariaLabel={t("StampClock.ReportedStarttime")}
                  value={startTimeStr}
                  onChange={canEditTimes ? setStartTimeStr : undefined}
                  disabled={!canEditTimes}
                  size="compact"
                  hasError={
                    canEditTimes &&
                    startTimeStr.length > 0 &&
                    !ShiftFormHHMMRegex.test(startTimeStr)
                  }
                  errorText={t("GigsCompany.EnterAValidTime")}
                />
              </div>
            </FlexRow>
            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                fontSize={FontSize.H4}
              >
                {t("StampClock.ReportedEndtime")}
              </Text>
              <div style={{ marginBottom: 12 }}>
                <TimePicker
                  ariaLabel={t("StampClock.ReportedEndtime")}
                  value={endTimeStr}
                  onChange={canEditTimes ? setEndTimeStr : undefined}
                  disabled={!canEditTimes}
                  size="compact"
                  hasError={
                    canEditTimes &&
                    endTimeStr.length > 0 &&
                    !ShiftFormHHMMRegex.test(endTimeStr)
                  }
                  errorText={t("GigsCompany.EnterAValidTime")}
                />
              </div>
            </FlexRow>

            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                fontSize={FontSize.H4}
              >
                {t("StampClock.ClockedIn")}
              </Text>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.H4}
              >
                {timereport.checkInTime
                  ? new Date(timereport.checkInTime).toLocaleTimeString(
                      "sv-SE",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )
                  : "-"}
              </Text>
            </FlexRow>

            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                fontSize={FontSize.H4}
              >
                {t("StampClock.ClockedOut")}
              </Text>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.H4}
              >
                {timereport.checkOutTime
                  ? new Date(timereport.checkOutTime).toLocaleTimeString(
                      "sv-SE",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )
                  : "-"}
              </Text>
            </FlexRow>
            <FlexRow>
              <Text
                fontFamily={FontFamily.MontserratSemiBold}
                fontSize={FontSize.H4}
              >
                {t("CompanyTimeReport.BreakMinTotal")}
              </Text>
              <Text
                fontFamily={FontFamily.MontserratRegular}
                fontSize={FontSize.H4}
              >
                {timereport.breakTime}
              </Text>
            </FlexRow>
            <div style={{ width: "100%", maxWidth: 420, marginBottom: 20 }}>
              <TextArea
                label={t("CompanyTimeReport.Notes")}
                value={userNotes}
                onChange={setUserNotes}
                placeholder={t("CompanyTimeReport.NotesPlaceHolder")}
                maxLength={500}
                maxHeight={240}
              />
            </div>
            <Row
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 12,
                marginTop: 20,
              }}
            >
              <SecondaryButton
                label={t("CompanyTimeReport.Cancel")}
                size="small"
                onClick={handleCloseModal}
              />

              <PrimaryButton
                label={t("CompanyTimeReport.Submit")}
                size="small"
                disabled={loading}
                onClick={handleSubmit}
              />
            </Row>
          </>
        )}
      </TimeReportModal>
    </Modal>
  );
};
